import { useEffect } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { Carousel, CarouselProps } from "@/components/confluence/carousel/src";
import { AnimatedJiraTour } from "@/components/product-demos/animated-jira-tour/src/animated-jira-tour";
import SiteActivation, {
    JoinableSites,
    ReturnToProduct
} from "@/components/signup/site-activation";
import { useUserContext } from "@/components/user-context";
import { Picture, PictureImageProps } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";
import {
    AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
    sendConversionEventAlgolia
} from "@/patterns/global-nav/src/auto-complete-helpers";
import { PaidAccountCreation } from "@/templates/paid-landing/sections/paid-account-creation.tsx";
import {
    BanditsHeaderWrapper,
    BanditsAssetWrapper
} from "@/utils/anchor-queries/bandits-components";
import { experiments, useExperiment } from "@/utils/experiment";
import { getLocalStorage } from "@/utils/localstorage";

const contentStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: "32px",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    width: "100%",

    [media.below.md]: {
        flexDirection: "column"
    }
});

const formColumn = css({
    display: "flex",
    flexDirection: "column",
    [media.above.md]: {
        maxWidth: "500px",
        width: "44%",
        minHeight: "650px"
    },
    [media.below.md]: {
        alignItems: "center"
    }
});

const signupFormContainer = css({
    maxWidth: "368px",
    width: "100%"
});

const headerStyle = css({
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "3rem",
    lineHeight: "3.5rem",
    fontWeight: 500,
    marginBlockStart: 50,
    marginBlockEnd: "24px",

    [media.below.md]: {
        textAlign: "center"
    }
});

const imgContainer = css({
    maxWidth: "650px",
    [media.above.md]: {
        width: "56%"
    },
    [media.below.md]: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column"
    }
});

const carouselContainerStyles = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    width: "700px",
    height: "600px",

    [media.below.sm]: {
        width: "100%",
        height: "100%"
    }
});

const subHeaderStyles = css({
    marginBlockEnd: "16px",
    textAlign: "center",
    [media.above.sm]: {
        textAlign: "left"
    }
});

const animationHeroContainer = css({
    position: "relative",
    aspectRatio: "800 / 550",
    height: "auto",
    minHeight: "1px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [media.below.lg]: {
        maxWidth: "100%"
    },
    [media.below.md]: {
        display: "none"
    }
});

const staticImageContainer = css({
    display: "none",
    [media.below.md]: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column"
    }
});

export interface PaidHeroProps {
    readonly header: string;
    readonly subHeader?: string;
    readonly image?: PictureImageProps;
    readonly showAnimatedHero?: boolean;
    readonly carouselHeroProps?: CarouselProps;
    readonly isAllTeamsTab?: boolean;
}

export const PaidHeroTabPanelTemplate = ({
    header,
    subHeader,
    image,
    showAnimatedHero = false,
    carouselHeroProps,
    isAllTeamsTab = false
}: PaidHeroProps) => {
    useEffect(() => {
        const getaaCompleteQueryObj = getLocalStorage(
            AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY
        );
        if (getaaCompleteQueryObj) {
            const parsedStringifyAttributes = JSON.parse(getaaCompleteQueryObj);
            sendConversionEventAlgolia({
                objectID: parsedStringifyAttributes.objectID,
                queryID: parsedStringifyAttributes.queryID,
                position: parsedStringifyAttributes.position
            });
        }
    }, []);

    const {
        user: { isUserLoggedIn }
    } = useUserContext();

    const contactExpandExperiment = useExperiment(
        experiments.jiraContactAndCompanyExpandSiteActivation
    );

    const useContactExpandCohortLogic =
        contactExpandExperiment.type === "enrolled" &&
        contactExpandExperiment.value;

    return (
        <Row>
            <div css={[contentStyle]} data-testid="hero-tab-panel">
                <div css={[formColumn]}>
                    <div>
                        <h1 css={[headerStyle]}>
                            <BanditsHeaderWrapper product="jira" type="header">
                                {header}
                            </BanditsHeaderWrapper>
                        </h1>
                    </div>
                    {subHeader && (
                        <span css={subHeaderStyles}>
                            <BanditsHeaderWrapper
                                product="jira"
                                type="subheader"
                            >
                                {subHeader}
                            </BanditsHeaderWrapper>
                        </span>
                    )}
                    <div css={signupFormContainer} data-testid="signup-form">
                        {isUserLoggedIn ? (
                            useContactExpandCohortLogic ? (
                                <SiteActivation
                                    CurrentProductUser={<ReturnToProduct />}
                                    CurrentProductJoinableUser={
                                        <JoinableSites />
                                    }
                                    useContactExpandCohortLogic={true}
                                />
                            ) : (
                                <SiteActivation />
                            )
                        ) : (
                            <PaidAccountCreation pageId="paid-lp-jsw-features" />
                        )}
                    </div>
                </div>
                <BanditsAssetWrapper
                    product="jira"
                    showChildren={!isAllTeamsTab}
                >
                    {showAnimatedHero && (
                        <div css={imgContainer}>
                            <div css={animationHeroContainer}>
                                <AnimatedJiraTour />
                            </div>
                        </div>
                    )}
                    {carouselHeroProps ? (
                        <div css={carouselContainerStyles}>
                            <Carousel {...carouselHeroProps} />
                        </div>
                    ) : null}
                    {image && (
                        <div
                            css={[
                                showAnimatedHero
                                    ? staticImageContainer
                                    : imgContainer
                            ]}
                        >
                            <Picture
                                image={image}
                                style={{ maxWidth: "100%" }}
                            />
                        </div>
                    )}
                </BanditsAssetWrapper>
            </div>
        </Row>
    );
};

PaidHeroTabPanelTemplate.displayName = "PaidHeroTemplate";
